import { GQL_ID } from '@@types'
import { useEffect, useMemo, useState } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { generatePath } from 'react-router'
import { useMediaQuery } from 'usehooks-ts'

import { useCategoriesBranch } from '@entities/Categories/api/categoriesBranch.gql'

import { useModalStore } from '@features/modal'
import { useAddressStore } from '@features/shipping-methods'

import { URL_PAGE_CATALOG_1ST_LVL, URL_PAGE_CATALOG_2ND_LVL } from '@shared/contants'
import { cn } from '@shared/lib/cn'
import { LocalizedLink as Link } from '@shared/lib/locale'

import styles from './styles.module.scss'


function ChildrenCategoriesLoading () {
  return (
    <SkeletonTheme>
      {Array.from({
        length: 12,
      }).map((_, index) => {
        return (
          <Skeleton
            width={'100%'}
            height={20}
            style={{
              borderRadius: 6,
            }}
            key={index}
          />
        )
      })}
    </SkeletonTheme>
  )
}

function CategoriesLoading () {
  return (
    <SkeletonTheme>
      <Skeleton
        count={10}
        width={170}
        height={30}
        style={{
          borderRadius: 6,
          marginBottom: 16,
        }}
      />
    </SkeletonTheme>
  )
}

export default function ModalCategories () {
  const isTablet = useMediaQuery('(max-width: 1179px)')
  const { closeModal } = useModalStore()
  const { shopId } = useAddressStore()
  const { categoriesBranch, loading: loadingCategoriesBranch } =
    useCategoriesBranch({
      shopId,
    })

  const [ activeCategoryId, setActiveCategoryId ] = useState<GQL_ID>()

  useEffect(() => {
    if (!activeCategoryId && categoriesBranch?.length) {
      setActiveCategoryId(categoriesBranch.find((c) => c?.slug)?.id)
    }
  }, [ categoriesBranch ])

  useEffect(() => {
    if (isTablet) {
      closeModal('categories')
    }
  }, [ isTablet ])

  const activeCategory = useMemo(() => categoriesBranch.find(i => i.id === activeCategoryId), [ activeCategoryId, categoriesBranch ])

  return (
    <div className={styles.root}>
      <div className={styles.root__left}>
        {loadingCategoriesBranch && <CategoriesLoading />}
        {!loadingCategoriesBranch &&
          categoriesBranch
            .map((item) => {
              return (
                <Link
                  key={item?.id}
                  className={cn(styles.root__link, {
                    [styles._active]: item?.id == activeCategoryId,
                  })}
                  to={generatePath(URL_PAGE_CATALOG_1ST_LVL, {
                    categorySlug: item?.slug,
                  })}
                  onClick={() => closeModal('categories')}
                  onMouseEnter={() => {
                    setActiveCategoryId(item?.id)
                  }}
                >
                  <img
                    className={styles.root__icon}
                    src={item?.icon?.url}
                    alt=""
                    loading="lazy"
                    fetchPriority="low"
                  />
                  {item?.name}
                </Link>
              )
            })}
      </div>

      {activeCategoryId && activeCategory && (
        <div className={styles.root__right}>
          {loadingCategoriesBranch && <ChildrenCategoriesLoading />}

          {!loadingCategoriesBranch && activeCategory.childrens && activeCategory.childrens.length > 0 &&
            activeCategory.childrens?.map((item, index) => {
              if (!item?.slug) return null
              return (
                <Link
                  className={styles.root__children}
                  to={generatePath(URL_PAGE_CATALOG_2ND_LVL, {
                    categorySlug: activeCategory.slug,
                    categoryChildrenSlug: item.slug
                  })}
                  onClick={() => closeModal('categories')}
                  key={`${item?.id ?? item?.slug ?? index}`}
                >
                  {item?.name}
                </Link>
              )
            })}
        </div>
      )}
    </div>
  )
}
